import { DEFAULT_PRODUCT_UNIT, PRODUCT_KINDS } from "@/constants/products";
import { SETTLEMENT_TYPES, SETTLEMENT_TYPE_LABELS, type SettlementType } from "@/constants/orders";
import { workspaceOrderPath } from "@/constants/routes";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState, LoadingState } from "@/components/shared/states";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useProducts, useProduct, type ProductListItem } from "@/features/products/api";
import { useCreateOrder } from "@/features/orders/api";
import { Dialog } from "@/components/ui/dialog";
import { formatCurrency, fromDateTimeLocalValue } from "@/lib/format";
import { useWorkspace } from "@/providers/workspace-provider";
import { computeOrderTotals } from "@bizy/core";
import type { ProductVariantRow } from "@bizy/core";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface CartLine {
  key: string;
  product: ProductListItem;
  variant: ProductVariantRow | null;
  quantity: number;
}

/** `/w/:slug/pos` — checkout desk: pick products, choose settlement, create order. */
export const PosPage = () => {
  const { workspace, settings } = useWorkspace();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState<CartLine[]>([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [settlementType, setSettlementType] = useState<SettlementType>(SETTLEMENT_TYPES.full);
  const [paidAmount, setPaidAmount] = useState("0");
  const [notes, setNotes] = useState("");
  const [dueAt, setDueAt] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const createOrder = useCreateOrder(workspace.id);

  const productsQuery = useProducts(workspace.id, {
    pageSize: 50,
    q: debouncedSearch.length > 0 ? debouncedSearch : undefined,
    activeOnly: true,
  });

  const totals = useMemo(
    () =>
      computeOrderTotals(
        lines.map((line) => ({
          unit_price: line.variant ? line.variant.price : line.product.price,
          quantity: line.quantity,
        })),
        settings.tax_percent,
      ),
    [lines, settings.tax_percent],
  );

  const effectivePaid =
    settlementType === SETTLEMENT_TYPES.full ? totals.total_amount : Number(paidAmount) || 0;

  const addLine = (product: ProductListItem, variant: ProductVariantRow | null = null) => {
    const key = `${product.id}:${variant?.id ?? "base"}`;

    // A bundle only exists if its components do: block overselling in the UI, the
    // Worker still re-checks inside the reservation transaction.
    if (product.kind === PRODUCT_KINDS.bundle) {
      const inCart = lines.find((line) => line.key === key)?.quantity ?? 0;
      if (inCart + 1 > (product.availability ?? 0)) {
        toast.error("Not enough component stock for another bundle.");
        return;
      }
    }

    setLines((current) => {
      const existing = current.find((line) => line.key === key);
      if (existing) {
        return current.map((line) =>
          line.key === key ? { ...line, quantity: line.quantity + 1 } : line,
        );
      }
      return [...current, { key, product, variant, quantity: 1 }];
    });
  };

  const changeQuantity = (key: string, delta: number) => {
    setLines((current) =>
      current
        .map((line) => (line.key === key ? { ...line, quantity: line.quantity + delta } : line))
        .filter((line) => line.quantity > 0),
    );
  };

  const onSubmit = async () => {
    if (customerName.trim().length < 2) {
      toast.error("Customer name is required.");
      return;
    }
    if (lines.length === 0) {
      toast.error("Add at least one product.");
      return;
    }

    try {
      const order = await createOrder.mutateAsync({
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim().length > 0 ? customerPhone.trim() : null,
        customer_email: customerEmail.trim().length > 0 ? customerEmail.trim() : null,
        settlement_type: settlementType,
        paid_amount: effectivePaid,
        notes: notes.trim().length > 0 ? notes.trim() : null,
        due_at: fromDateTimeLocalValue(dueAt),
        items: lines.map((line) => ({
          product_id: line.product.id,
          variant_id: line.variant?.id ?? null,
          quantity: line.quantity,
        })),
      });
      toast.success(`Order ${order.code} created.`);
      navigate(workspaceOrderPath(workspace.slug, order.id));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create the order.");
    }
  };

  const [variantPicker, setVariantPicker] = useState<ProductListItem | null>(null);
  const productDetail = useProduct(workspace.id, variantPicker?.id);

  return (
    <>
      <PageHeader title="Point of sale" description={`${workspace.name} · tax ${settings.tax_percent}%`} />

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3">
            <CardTitle>Catalog</CardTitle>
            <Input
              placeholder="Search products…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="max-w-56"
            />
          </CardHeader>
          <CardContent>
            {productsQuery.isLoading ? <LoadingState label="Loading catalog…" /> : null}
            {!productsQuery.isLoading && (productsQuery.data?.items.length ?? 0) === 0 ? (
              <EmptyState
                title="No products available"
                description="Add products to the catalog before selling."
              />
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {(productsQuery.data?.items ?? []).map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() =>
                    product.kind === PRODUCT_KINDS.bundle
                      ? addLine(product)
                      : setVariantPicker(product)
                  }
                  className="rounded-card border border-border p-3 text-left transition-colors hover:border-primary/60 hover:bg-muted"
                >
                  <p className="truncate text-sm font-medium">{product.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(product.price, settings.currency)} ·{" "}
                    {product.unit || DEFAULT_PRODUCT_UNIT}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {product.kind === PRODUCT_KINDS.bundle
                      ? `${product.availability ?? 0} bundles buildable`
                      : `stock ${product.stock}`}
                  </p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:sticky lg:top-20 lg:self-start">
          <CardHeader>
            <CardTitle>Cart</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {lines.length === 0 ? (
              <EmptyState title="Cart is empty" description="Pick a product to start an order." />
            ) : (
              <div className="space-y-2">
                {lines.map((line) => (
                  <div key={line.key} className="flex items-center gap-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{line.product.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {line.variant ? `${line.variant.name} · ` : ""}
                        {formatCurrency(
                          line.variant ? line.variant.price : line.product.price,
                          settings.currency,
                        )}
                      </p>
                      {line.product.components.length > 0 ? (
                        <p className="text-[11px] text-muted-foreground">
                          {line.product.components
                            .map((component) => `${component.name} × ${component.quantity}`)
                            .join(" · ")}
                        </p>
                      ) : null}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      className="size-7"
                      onClick={() => changeQuantity(line.key, -1)}
                    >
                      <Minus className="size-3" aria-hidden />
                    </Button>
                    <span className="w-6 text-center tabular-nums">{line.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="size-7"
                      onClick={() => changeQuantity(line.key, 1)}
                    >
                      <Plus className="size-3" aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-danger"
                      onClick={() => changeQuantity(line.key, -line.quantity)}
                    >
                      <Trash2 className="size-3" aria-hidden />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-1 border-t border-border pt-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">
                  {formatCurrency(totals.subtotal, settings.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax</span>
                <span className="tabular-nums">
                  {formatCurrency(totals.tax_amount, settings.currency)}
                </span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span className="tabular-nums">
                  {formatCurrency(totals.total_amount, settings.currency)}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <Field label="Customer" htmlFor="pos-customer" required>
                <Input
                  id="pos-customer"
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" htmlFor="pos-phone">
                  <Input
                    id="pos-phone"
                    value={customerPhone}
                    onChange={(event) => setCustomerPhone(event.target.value)}
                  />
                </Field>
                <Field label="Email" htmlFor="pos-email">
                  <Input
                    id="pos-email"
                    value={customerEmail}
                    onChange={(event) => setCustomerEmail(event.target.value)}
                  />
                </Field>
              </div>

              <Field label="Settlement" htmlFor="pos-settlement">
                <Select
                  id="pos-settlement"
                  value={settlementType}
                  onChange={(event) => {
                    const value = event.target.value as SettlementType;
                    setSettlementType(value);
                    if (value === SETTLEMENT_TYPES.full) setPaidAmount(String(totals.total_amount));
                  }}
                >
                  {Object.values(SETTLEMENT_TYPES).map((value) => (
                    <option key={value} value={value}>
                      {SETTLEMENT_TYPE_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </Field>

              {settlementType === SETTLEMENT_TYPES.downPayment ? (
                <Field
                  label="Down payment"
                  htmlFor="pos-paid"
                  hint={`Balance ${formatCurrency(
                    totals.total_amount - (Number(paidAmount) || 0),
                    settings.currency,
                  )}`}
                >
                  <Input
                    id="pos-paid"
                    inputMode="decimal"
                    value={paidAmount}
                    onChange={(event) => setPaidAmount(event.target.value)}
                  />
                </Field>
              ) : null}

              <Field label="Due date" htmlFor="pos-due">
                <Input
                  id="pos-due"
                  type="datetime-local"
                  value={dueAt}
                  onChange={(event) => setDueAt(event.target.value)}
                />
              </Field>

              <Field label="Notes" htmlFor="pos-notes">
                <Textarea
                  id="pos-notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </Field>

              <Button
                className="w-full"
                loading={createOrder.isPending}
                disabled={lines.length === 0}
                onClick={() => void onSubmit()}
              >
                Create order
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={variantPicker !== null}
        onClose={() => setVariantPicker(null)}
        title={variantPicker?.name ?? "Select variant"}
        description="Choose a variant, or sell the base product."
      >
        <div className="space-y-2">
          <button
            type="button"
            className="w-full rounded-lg border border-border p-3 text-left text-sm hover:bg-muted"
            onClick={() => {
              if (variantPicker) addLine(variantPicker, null);
              setVariantPicker(null);
            }}
          >
            Base product · {formatCurrency(variantPicker?.price ?? 0, settings.currency)}
          </button>
          {(productDetail.data?.variants ?? []).map((variant) => (
            <button
              key={variant.id}
              type="button"
              className="w-full rounded-lg border border-border p-3 text-left text-sm hover:bg-muted"
              onClick={() => {
                if (variantPicker) addLine(variantPicker, variant);
                setVariantPicker(null);
              }}
            >
              {variant.name} · {formatCurrency(variant.price, settings.currency)}
            </button>
          ))}
          {productDetail.isLoading ? <LoadingState label="Loading variants…" /> : null}
        </div>
      </Dialog>
    </>
  );
};
